const CREDENTIAL_TOKENS = new Set([
  'ACC',
  'CSM',
  'JD',
  'MD',
  'PCC',
  'PE',
  'RN',
  'SCP',
  'ASM',
  'CASP',
  'CFA',
  'CFP',
  'CIPD',
  'CISA',
  'CISM',
  'CISSP',
  'CMA',
  'CPHIMS',
  'CPA',
  'CPHR',
  'DBA',
  'DMD',
  'DPA',
  'MBA',
  'DDS',
  'EDD',
  'ESQ',
  'ITIL',
  'MPH',
  'MSW',
  'PHD',
  'PHR',
  'PMP',
  'PSYD',
  'SCMP',
  'SHRM',
  'SPHR',
  'GPHR',
]);

const INSTITUTION_KEYWORDS = [
  'academy',
  'college',
  'institute',
  'school',
  'university',
];

const EMOJI_REGEX = /[\p{Extended_Pictographic}\uFE0F\u200D]/gu;

const normalizeToken = (value: string) =>
  value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

const isUppercaseToken = (value: string) => {
  const letters = value.match(/[A-Za-z]/g);
  if (!letters || letters.length === 0) return false;
  return letters.every((letter) => letter === letter.toUpperCase());
};

const isCredentialToken = (value: string) => {
  const normalized = normalizeToken(value);
  if (!normalized) return false;
  if (CREDENTIAL_TOKENS.has(normalized)) return true;

  if (
    normalized.length >= 2 &&
    normalized.length <= 8 &&
    (isUppercaseToken(value) || value.includes('.'))
  ) {
    return true;
  }

  return false;
};

const isCredentialValue = (value: string) => {
  const hyphenParts = value
    .split('-')
    .map((part) => part.trim())
    .filter(Boolean);
  if (hyphenParts.length > 1) {
    return hyphenParts.every(isCredentialToken);
  }
  return isCredentialToken(value);
};

const isInstitutionSegment = (value: string) =>
  INSTITUTION_KEYWORDS.some((keyword) => value.toLowerCase().includes(keyword));

const isCredentialSegment = (value: string) => {
  const tokens = value.split(/[\s/|&]+/).filter(Boolean);
  if (tokens.length === 0) return false;
  return tokens.every(isCredentialValue);
};

const stripTrailingCredentials = (value: string) => {
  let cleaned = value.trim();

  while (true) {
    const match = cleaned.match(/^(.*)\(([^)]+)\)\s*$/);
    if (!match) break;
    const base = match[1].trim();
    const inner = match[2].trim();
    if (!isCredentialSegment(inner) && !isInstitutionSegment(inner)) break;
    cleaned = base;
  }

  const parts = cleaned.split(',').map((part) => part.trim());
  while (
    parts.length > 1 &&
    (isCredentialSegment(parts[parts.length - 1]) ||
      isInstitutionSegment(parts[parts.length - 1]))
  ) {
    parts.pop();
  }
  cleaned = parts.join(', ').trim();

  const separatorRegex = /^(.*?)(?:\s+[|/-]\s+)([^|/-]+)$/;
  let separatorMatch = cleaned.match(separatorRegex);
  while (separatorMatch && isCredentialSegment(separatorMatch[2])) {
    cleaned = separatorMatch[1].trim();
    separatorMatch = cleaned.match(separatorRegex);
  }

  return cleaned.replace(/[\s,|/-]+$/g, '').trim();
};

export const normalizePersonName = (
  value: string | null | undefined
): string => {
  if (!value) return '';
  const cleaned = value.replace(EMOJI_REGEX, '').replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  return stripTrailingCredentials(cleaned);
};

const HONORIFICS = new Set(['DR', 'MR', 'MRS', 'MS', 'MX', 'MISS', 'PROF']);
const SUFFIXES = new Set(['JR', 'SR', 'II', 'III', 'IV']);
const INITIAL = /^[A-Z]\.?$/;

/** Matches the backend person-name splitter without shortening surnames. */
export function splitPersonName(
  fullName?: string | null,
  first?: string | null,
  last?: string | null
): [string | null, string | null] {
  const words = (value?: string | null): string[] => {
    const text = (value ?? '')
      .replace(EMOJI_REGEX, '')
      .replace(/\([^)]*\)|"[^"]*"|“[^”]*”/g, ' ')
      .split(/\s+[|/-]\s+/)[0];
    const [name, ...tails] = text.split(',');
    const parts = name.trim().split(/\s+/).filter(Boolean);
    for (const tail of tails) {
      const [suffix, ...rest] = tail.trim().split(/\s+/);
      if (!SUFFIXES.has(normalizeToken(suffix))) break;
      parts.push(suffix);
      if (rest.length) break;
    }
    while (parts.length && HONORIFICS.has(normalizeToken(parts[0])))
      parts.shift();
    while (
      parts.length &&
      parts[parts.length - 1]
        .split('-')
        .every((part) => CREDENTIAL_TOKENS.has(normalizeToken(part)))
    )
      parts.pop();
    return parts;
  };
  const withoutInitials = (parts: string[]): string[] => {
    const kept = parts.filter((part) => !INITIAL.test(part));
    return kept.every((part) => SUFFIXES.has(normalizeToken(part)))
      ? parts
      : kept;
  };
  const cleanPart = (value?: string | null): string | null => {
    const parts = withoutInitials(words(value));
    return parts.length &&
      !parts.every(
        (part) => INITIAL.test(part) || SUFFIXES.has(normalizeToken(part))
      )
      ? parts.join(' ')
      : null;
  };
  const parts = words(fullName);
  return [
    cleanPart(first) || cleanPart(parts[0]),
    cleanPart(last) ||
      (parts.length >= 2
        ? withoutInitials(parts.slice(1)).join(' ') || null
        : null),
  ];
}
